import React, { useEffect, useState } from "react";
import { Smartphone, ExternalLink, Loader2 } from "lucide-react";
import { checkAppVersion } from "../lib/appVersionCheck";
import { openExternalUrl } from "../lib/openExternal";

/**
 * ForceUpdateGate — mounted once at the app root.
 *
 * On native shells only, calls `/api/app-version/check` on boot and
 * renders a non-dismissible modal when the Super Admin has enabled
 * `force_update` for the current platform AND the installed
 * versionCode < the configured minimum.
 *
 * If Force Update is OFF, the endpoint returns `force: false` and this
 * component renders nothing — even if a newer version exists.
 *
 * Web builds short-circuit inside `checkAppVersion()` and this
 * component never renders anything.
 */
export default function ForceUpdateGate() {
  const [state, setState] = useState(null); // null | { force, ...cfg }

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await checkAppVersion();
        if (cancelled) return;
        if (r && r.force === true) setState(r);
      } catch (_) { /* silent — never block the app on this */ }
    })();
    return () => { cancelled = true; };
  }, []);

  if (!state || !state.force) return null;

  return (
    <div
      className="fixed inset-0 flex items-end sm:items-center justify-center px-0 sm:px-4"
      style={{ background: "rgba(16,16,16,0.85)", zIndex: 2147483000 }}
      data-testid="force-update-modal"
      onClick={(e) => e.stopPropagation()}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="force-update-title"
        className="w-full sm:max-w-md bg-white border-2 border-ink shadow-brutalSm p-6 sm:p-8"
        style={{ marginBottom: "env(safe-area-inset-bottom)" }}
      >
        <div className="flex items-center gap-3 mb-4">
          <div className="w-12 h-12 border-2 border-ink bg-coral text-white flex items-center justify-center flex-shrink-0">
            <Smartphone className="w-6 h-6" strokeWidth={2.5} />
          </div>
          <div className="min-w-0">
            <p className="text-[11px] uppercase tracking-widest font-bold text-ink/60">
              Update required
            </p>
            <h2
              id="force-update-title"
              className="font-display font-black text-xl sm:text-2xl tracking-tight"
            >
              A new version of Linkola is available
            </h2>
          </div>
        </div>

        <p className="text-sm text-ink/80 leading-relaxed mb-5">
          {state.message ||
            "Please update to continue using the app. This update improves reliability and adds new features."}
        </p>

        <dl className="grid grid-cols-2 gap-3 mb-6 text-xs">
          <div className="border-2 border-ink bg-cream p-2.5">
            <dt className="uppercase tracking-wider font-bold text-ink/60">Your version</dt>
            <dd className="font-mono font-bold mt-0.5" data-testid="force-update-current">
              {state.client_version_name || state.client_version_code || "-"}
            </dd>
          </div>
          <div className="border-2 border-ink bg-mint p-2.5">
            <dt className="uppercase tracking-wider font-bold text-ink/60">Required</dt>
            <dd className="font-mono font-bold mt-0.5" data-testid="force-update-required">
              {state.latest_version_name || state.latest_version_code || "-"}
            </dd>
          </div>
        </dl>

        <button
          type="button"
          onClick={() => openExternalUrl(state.update_url)}
          data-testid="force-update-open-store"
          className="w-full inline-flex items-center justify-center gap-2 border-2 border-ink bg-coral text-white px-4 py-3 font-display font-black uppercase tracking-wide shadow-brutalSm hover:-translate-y-0.5 transition-transform"
        >
          <ExternalLink className="w-4 h-4" strokeWidth={2.5} />
          {state.client_platform === "ios" ? "Open App Store" : "Open Play Store"}
        </button>

        <p className="mt-4 text-[11px] text-ink/50 text-center">
          This update is mandatory and the app cannot be used until it is installed.
        </p>
      </div>
    </div>
  );
}
